import React, { useState, useContext, useMemo, useRef, useCallback } from "react";
import { ProfileContext } from "../context/ProfileContext";
import { CrewContext } from "../context/CrewContext";
import { RouteComponentProps, useHistory } from "react-router-dom";
import {
  IonContent,
  IonPage,
  IonGrid,
  IonItem,
  IonList,
  IonCheckbox,
  IonAvatar,
  IonSearchbar,
  IonFooter,
  IonToolbar,
  IonButton,
  IonLabel,
  IonBadge,
  IonAccordionGroup,
  IonAccordion,
  IonText,
  useIonAlert,
  useIonToast,
} from "@ionic/react";
import "../theme/AppStyles.css";
import { WerkerSchedulerHeader } from "../components/WerkHeader";
import { filterOutSupportMonkey } from "../constants/supportConstants";
import { createAssociateSearchFilter, sortAssociatesByName } from "../utils/associateFilters";
import { setPendingAudienceSelection } from "../domain/announcements/announcementAudienceHandoff";

type AssociateAudienceRow = {
  WerkerUid: string;
  WerkerFirstName: string;
  WerkerLastName: string;
  WerkerProfilePicURL: string;
  WerkerCompany: string;
  WerkerBio: string;
  isSelected: boolean;
};

type LocationState = {
  cameFrom?: string;
  selectedAssociates?: any[];
  selectedCrewIds?: string[];
  draftTitle?: string;
  draftBody?: string;
  draftImageUrl?: string | null;
  draftIsPinned?: boolean;
  mode?: "create" | "edit";
  announcementId?: string;
  recipientUids?: string[];
};

const SUPPORT_MONKEY_UID = "y4Evz9nRBMTgNp9syD3SRvuBO0u2";
const EMPTY_ASSOCIATES: any[] = [];
const EMPTY_CREW_IDS: string[] = [];

const SelectAnnouncementAudience: React.FC<RouteComponentProps> = () => {
  const history = useHistory<LocationState>();
  const { myAssociates } = useContext(ProfileContext);
  const { crews, crewsLoading } = useContext(CrewContext);

  const [searchText, setSearchText] = useState("");
  const [presentAlert] = useIonAlert();
  const [presentToast] = useIonToast();
  const [crewAccordionValue, setCrewAccordionValue] = useState<string | undefined>(undefined);

  const locationState = (history.location.state || {}) as LocationState;
  const preselectedAssociates = Array.isArray(locationState.selectedAssociates)
    ? locationState.selectedAssociates
    : EMPTY_ASSOCIATES;
  const preselectedCrewIds = Array.isArray(locationState.selectedCrewIds) ? locationState.selectedCrewIds : EMPTY_CREW_IDS;

  const contentRef = useRef<HTMLIonContentElement | null>(null);
  const savedScrollY = useRef<number>(0);
  const rafId = useRef<number | null>(null);

  const onContentScroll = useCallback((e: CustomEvent) => {
    // @ts-ignore ionic typing for detail.scrollTop
    savedScrollY.current = e.detail?.scrollTop ?? 0;
  }, []);

  const restoreScroll = useCallback(() => {
    if (!contentRef.current) return;
    if (rafId.current) cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() => {
      contentRef.current?.scrollToPoint(0, savedScrollY.current, 0);
    });
  }, []);

  const initialAudienceRows = useMemo<AssociateAudienceRow[]>(() => {
    const selectedUidSet = new Set<string>(
      preselectedAssociates
        .map((a: any) => String(a?.WerkerUid || ""))
        .filter((uid: string) => uid.length > 0)
    );

    const base = Array.isArray(myAssociates) ? myAssociates : [];

    return filterOutSupportMonkey(base)
      .filter((val: any) => !!val?.WerkerUid && val.WerkerUid !== SUPPORT_MONKEY_UID)
      .map((val: any) => ({
        WerkerUid: val.WerkerUid,
        WerkerFirstName: val.WerkerFirstName || "",
        WerkerLastName: val.WerkerLastName || "",
        WerkerProfilePicURL: val.WerkerProfilePicURL || "../assets/profilePic.png",
        WerkerCompany: val.WerkerCompany || "",
        WerkerBio: val.WerkerBio || "",
        isSelected: selectedUidSet.has(val.WerkerUid),
      }));
  }, [myAssociates, preselectedAssociates]);

  const [audienceRows, setAudienceRows] = useState<AssociateAudienceRow[]>(initialAudienceRows);
  const [selectedCrewIds, setSelectedCrewIds] = useState<string[]>(preselectedCrewIds);

  React.useEffect(() => {
    setAudienceRows(initialAudienceRows);
  }, [initialAudienceRows]);

  const filteredSorted = useMemo(() => {
    const text = (searchText || "").toLowerCase().trim();

    const filtered = !text
      ? audienceRows
      : audienceRows.filter(createAssociateSearchFilter(searchText));

    return sortAssociatesByName(filtered);
  }, [audienceRows, searchText]);

  const allFilteredSelected = useMemo(() => {
    return filteredSorted.length > 0 && filteredSorted.every((x) => x.isSelected === true);
  }, [filteredSorted]);

  const someFilteredSelected = useMemo(() => {
    if (filteredSorted.length === 0) return false;
    const selectedCount = filteredSorted.filter((x) => x.isSelected === true).length;
    return selectedCount > 0 && selectedCount < filteredSorted.length;
  }, [filteredSorted]);

  const visibleUidsCount = useMemo(() => filteredSorted.length, [filteredSorted]);

  const visibleSelectedCount = useMemo(() => {
    return filteredSorted.filter((x) => x.isSelected === true).length;
  }, [filteredSorted]);

  const totalSelectedCount = useMemo(() => {
    return audienceRows.filter((x) => x.isSelected === true).length;
  }, [audienceRows]);

  const applySelectAll = useCallback(
    (checked: boolean) => {
      const filteredUIDs = new Set(filteredSorted.map((u) => u.WerkerUid));
      restoreScroll();
      setAudienceRows((prev) =>
        prev.map((u) => (filteredUIDs.has(u.WerkerUid) ? { ...u, isSelected: checked } : u))
      );
      restoreScroll();
    },
    [filteredSorted, restoreScroll]
  );

  const handleSelectAllClick = () => {
    applySelectAll(!allFilteredSelected);
  };

  const handleAudienceToggle = (uid: string, e: any) => {
    const checked =
      (e?.detail && typeof e.detail.checked === "boolean" && e.detail.checked) ??
      (typeof e?.target?.checked === "boolean" && e.target.checked) ??
      false;

    restoreScroll();
    setAudienceRows((prev) => prev.map((u) => (u.WerkerUid === uid ? { ...u, isSelected: checked } : u)));
    restoreScroll();
  };

  const getInitials = (name: string) => {
    const parts = (name || "").trim().split(" ").filter(Boolean);
    if (parts.length === 0) return "C";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const applyCrewToSelection = useCallback(
    async (crew: any) => {
      const crewName = (crew?.name || "").trim() || "this crew";
      const rawIds = Array.isArray(crew?.memberIds) ? crew.memberIds : [];

      const crewIds = rawIds
        .filter((v: any) => typeof v === "string" && v.length > 0)
        .filter((id: string) => id !== SUPPORT_MONKEY_UID);

      if (crewIds.length === 0) {
        await presentAlert({
          header: "Crew is Empty",
          message: `“${crewName}” has no members yet.`,
          buttons: ["OK"],
        });
        return;
      }

      await presentAlert({
        header: "Add Whole Crew?",
        message: `Add all members of “${crewName}” to this announcement audience?`,
        buttons: [
          { text: "Cancel", role: "cancel" },
          {
            text: "Add Crew",
            handler: () => {
              restoreScroll();

              let addedCount = 0;
              const set = new Set(crewIds);

              setAudienceRows((prev) =>
                prev.map((u) => {
                  if (set.has(u.WerkerUid) && !u.isSelected) {
                    addedCount += 1;
                    return { ...u, isSelected: true };
                  }
                  return u;
                })
              );

              setSelectedCrewIds((prev) => {
                const next = new Set(prev);
                if (crew?.id) next.add(crew.id);
                return Array.from(next);
              });

              restoreScroll();
              setCrewAccordionValue(undefined);

              presentToast({
                message: `Added ${addedCount} from ${crewName}`,
                duration: 1500,
                position: "top",
                color: "medium",
              });
            },
          },
        ],
      });
    },
    [presentAlert, presentToast, restoreScroll]
  );

  const handleContinue = () => {
    const selectedAssociates = audienceRows.filter((row) => row.isSelected);

    // Hand the selections back to the existing Announcement compose entry via a
    // module-level store, then goBack(). This avoids creating a second
    // /Announcement history entry (which would visually look identical to the
    // first and make the back arrow appear to do nothing on the first press).
    setPendingAudienceSelection({
      selectedAssociates,
      selectedCrewIds,
    });

    history.goBack();
  };

  return (
    <IonPage>
      <WerkerSchedulerHeader title={"Select Audience"} />

      <IonContent
        ref={contentRef}
        scrollEvents={true}
        onIonScroll={onContentScroll}
      >
        <IonSearchbar
          placeholder="Filter by #Tag, name, company, etc..."
          value={searchText}
          onIonChange={(e: any) => setSearchText(e.detail.value!)}
        />

        <IonAccordionGroup
          value={crewAccordionValue}
          onIonChange={(e) => setCrewAccordionValue((e.detail.value as string) || undefined)}
        >
          <IonAccordion
            value="crews"
          >
            <IonItem
              slot="header"
              lines="full"
            >
              <IonLabel>
                <strong>Add Crew</strong>
              </IonLabel>

              <IonBadge
                slot="end"
                color="medium"
              >
                {crews?.length || 0}
              </IonBadge>
            </IonItem>

            <div slot="content">
              {crewsLoading ? (
                <IonItem
                  lines="none"
                >
                  <IonLabel
                    color="medium"
                  >
                    Loading crews...
                  </IonLabel>
                </IonItem>
              ) : !crews || crews.length === 0 ? (
                <IonItem
                  lines="none"
                >
                  <IonLabel
                    color="medium"
                  >
                    No crews yet.
                  </IonLabel>
                </IonItem>
              ) : (
                <IonList>
                  {crews.map((crew: any) => (
                    <IonItem
                      key={crew.id}
                      button={true}
                      detail={false}
                      onClick={() => applyCrewToSelection(crew)}
                    >
                      <IonAvatar
                        slot="start"
                      >
                        {crew.avatarUrl ? (
                          <img
                            src={crew.avatarUrl}
                            alt={crew.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 800,
                              fontSize: "14px",
                              background: "#e5e5e5",
                              color: "#333",
                            }}
                          >
                            {getInitials(crew.name)}
                          </div>
                        )}
                      </IonAvatar>

                      <IonLabel
                        className="ion-text-wrap"
                      >
                        <strong>{crew.name}</strong>

                        <div style={{ marginTop: "2px" }}>
                          <IonText
                            color="medium"
                          >
                            {crew.description && crew.description.trim().length > 0
                              ? crew.description
                              : "No description"}
                          </IonText>
                        </div>
                      </IonLabel>

                      <IonBadge
                        slot="end"
                        color="medium"
                      >
                        {crew.memberCount}
                      </IonBadge>
                    </IonItem>
                  ))}
                </IonList>
              )}
            </div>
          </IonAccordion>
        </IonAccordionGroup>

        <IonGrid>
          <IonItem
            button={true}
            detail={false}
            onClick={handleSelectAllClick}
          >
            <IonLabel>
              {searchText?.trim() ? "Select All (filtered)" : "Select All"}

              <IonText
                color="medium"
              >
                <div style={{ marginTop: "2px", fontSize: "13px" }}>
                  {visibleSelectedCount} selected / {visibleUidsCount} associates
                </div>
              </IonText>
            </IonLabel>

            <IonCheckbox
              slot="end"
              checked={allFilteredSelected}
              indeterminate={someFilteredSelected}
              disabled={true}
            />
          </IonItem>

          <IonList
            className="searchBar"
          >
            {filteredSorted.map((val) => (
              <IonItem
                key={val.WerkerUid}
                className="searchBar"
              >
                <IonAvatar
                  slot="start"
                >
                  <img src={val.WerkerProfilePicURL} alt="" />
                </IonAvatar>

                <IonLabel
                >
                  <h2>
                    {val.WerkerFirstName} {val.WerkerLastName}
                  </h2>
                  <p>{val.WerkerCompany}</p>
                </IonLabel>

                <IonCheckbox
                  slot="end"
                  checked={val.isSelected}
                  onIonChange={(e) => handleAudienceToggle(val.WerkerUid, e)}
                />
              </IonItem>
            ))}
          </IonList>
        </IonGrid>
      </IonContent>

      <IonFooter>
        <IonToolbar>
          <IonButton
            expand="block"
            color="seconwarning"
            onClick={handleContinue}
            disabled={totalSelectedCount === 0}
          >
            Continue ({totalSelectedCount})
          </IonButton>
          <br />
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default SelectAnnouncementAudience;